import * as THREE from "three";
import { assetUrl } from "./assetVer.js";
import { session } from "./auth/session.js";
import { verifyEmail } from "./auth/api.js";

// Cache-bust the fixed-name public/ assets after a deploy: route every THREE loader URL (scene props,
// NPC sheets, terrain, …) through assetUrl so a re-render's new ?v= forces a fresh download instead of
// serving the stale CDN/browser copy. (The player's c3d sheets load via Image — versioned in c3dImage.)
THREE.DefaultLoadingManager.setURLModifier((url) => assetUrl(url));
import { showAuthOverlay } from "./ui/authOverlay.js";
import { showVerifyEmail } from "./ui/verifyEmail.js";
import { showCharacterSelect } from "./ui/characterSelect.js";
import { hideDiscordBadge } from "./ui/discordBadge.js";
import { listCharacters } from "./characters/api.js";
import { showStatus } from "./ui/statusOverlay.js";
import { onRequestSelection } from "./appFlow.js";
import { mountFullscreenButton } from "./ui/fullscreen.js";
import { isMobile, showMobileGate } from "./ui/mobileGate.js";
import { World3D } from "./three/world3d.js";

let world: World3D | null = null;

/** If the page was opened from a confirmation-email link (`?verify=…`), confirm the email,
 *  then strip the token from the URL so a refresh doesn't re-run it. */
async function handleVerifyLink(): Promise<void> {
  const url = new URL(location.href);
  const token = url.searchParams.get("verify");
  if (!token) return;
  try { await verifyEmail(token); } catch { /* invalid/expired — the verify screen can resend */ }
  url.searchParams.delete("verify");
  history.replaceState({}, "", url.pathname + url.search + url.hash);
}

/** Show character selection, then enter the 3D world. Returns "logout" if the player logs out. */
async function selectAndPlay(): Promise<"logout" | void> {
  const characters = await listCharacters();
  const choice = await showCharacterSelect(characters);
  if (choice === "logout") return "logout";
  session.character = choice;
  hideDiscordBadge(); // leave the pre-game screens → drop the corner Discord badge
  showStatus("Entering the world…", { spinner: true });
  const mount = document.getElementById("game")!;
  world = new World3D(mount);
  world.start();
}

/** Ensure the player is signed in AND email-confirmed, then select a character + play. Logging
 *  out (from the verify screen or selection) loops back to the auth overlay. */
async function run(): Promise<void> {
  for (;;) {
    if (!session.isAuthenticated) await showAuthOverlay();
    // Unverified accounts are gated out of character creation until they confirm their email.
    if (session.emailVerified === false) {
      if ((await showVerifyEmail()) === "logout") { await session.logout(); continue; }
    }
    if ((await selectAndPlay()) === "logout") { await session.logout(); continue; }
    return; // in the world
  }
}

async function start(): Promise<void> {
  // Mobile isn't ready yet — show the "under construction" notice instead of booting the 3D world.
  if (isMobile()) { showMobileGate(); return; }
  // Suppress the browser's native right-click menu everywhere — the game uses right-click/drag
  // for its own interactions, and the OS context menu just gets in the way.
  window.addEventListener("contextmenu", (e) => e.preventDefault());
  mountFullscreenButton();
  await handleVerifyLink();
  await session.bootstrap(); // restore a session from the httpOnly refresh cookie (if any)

  // In-world "Switch character" tears down the world and returns to selection.
  onRequestSelection(() => {
    world?.destroy();
    world = null;
    void run();
  });

  await run();
}

void start();
